import classnames from 'classnames';
import { action } from 'mobx';
import React from 'react';

import { I18nContent, Internationalized, LangCode } from '../../../adl-gen/totalinteraction/futurefit/db';
import { Entry, RepositoryEntries } from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { RepositoryPutEntry } from '../../../adl-gen/totalinteraction/futurefit/requests';
import { Box, GridContainer, Sizes } from '../../../ui/common/grid-container/grid-container';
import { CloseIcon, PlusIcon, TickCircleIcon } from '../../../ui/common/icons';
import { Button } from '../../../ui/widgets/button/button';
import { CheckItem } from '../../../ui/widgets/check-item/check-item';
import { SimpleMDEReactContainer } from '../../../ui/widgets/easy-markdown-editor/easy-markdown-editor';
import { MultiSelect, MultiSelectBox, Select, SelectBox, SelectOption } from '../../../ui/widgets/select/select';
import { TableCell } from '../../../ui/widgets/table/table';
import { FieldState, newTextField } from '../../../ui/widgets/text-input/text-field';
import { InputType, TextInput } from '../../../ui/widgets/text-input/text-input';
import * as FileUtility from '../../../util/file-repos-utility';
import { WithId } from '../../../util/utils';
import { CSVDropzone, FileContent } from '../components/dropzone/dropzone';
import { Message } from '../widgets/message/message';

const styles = require('./editor.css');
const fonts = require('../../../ui/common/fonts.css');

// /// Not exported from TextInput (grrr)
// export type InputType =
//   | "text"
//   | "textarea"
//   | "password"
//   | "number"
//   | "tel"
//   | "email"
//   | "date"
//   | "datetime-local"

export type I18nInput = Internationalized<FieldState<string | number>>;

export type AnswerType =
  | 'singleResponse'
  | 'multiResponse'
  | 'orderedResponse'
  | 'submitted'
  | 'embedded'
  | 'selfAssessmentResponse';

/// This maps the 'isNotApplicableAnswer' as a temporary default
/// The intention is when a new set of Answers is built, and one
/// is a 'Not applicable' answer, we can default the scoring for that answer
/// to zero. However, it is not saved in the database and isn't a fundamental data type.
/// Similarly WithId is deliberately not a database artifact, but used to map together items
/// in independent arrays which can be reordered.
export interface InputAnswer<T> {
  isNotApplicableAnswer: boolean;
  answer: WithId<T>;
}

export const isEmpty = (field: FieldState<string | number>): boolean => {
  return field.get().kind === 'empty' || field.value().length === 0;
};

export const GridWrapper = (
  /// TODO Make this a full Sizes object
  sizes: Sizes,
  child: JSX.Element | null,
  debug?: boolean
) => (
  <GridContainer debug={debug} xsSpan={sizes.xsSpan}>
    {child}
  </GridContainer>
);

export const DisplayText = (params: {
  field: string | number;
  title: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <p className={classnames(fonts.captionSmall, fonts.left)}>{params.field}</p>
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const DisplayI18nText = (params: {
  field: I18nContent;
  lang: LangCode;
  title: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <p className={classnames(fonts.captionSmall, fonts.left)}>{params.field.value[params.lang]}</p>
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

interface DisplayImgProps {
  field: string;
  title?: string;
  xsSpan: Sizes['xsSpan'];
  height?: number;
  debug?: boolean;
  isMandatory?: boolean;
}
export const DisplayImg = ({
  field,
  title,
  xsSpan,
  height,
  debug,
  isMandatory,
}: DisplayImgProps) => {
  const titleBox = title ? (
    <Box>
      <p className={classnames(fonts.caption, isMandatory && styles.mandatory)}>{title}</p>
    </Box>
  ) : null;
  const boxHeight = height || 100;
  const element = (
    <Box>
      <img height={boxHeight} src={field} className={styles.displayImg} />
    </Box>
  );
  return (
    <>
      {GridWrapper({ xsSpan: xsSpan }, titleBox, debug)}
      {GridWrapper({ xsSpan: xsSpan }, element, debug)}
    </>
  );
};

export const DisplayCheckItem = (params: {
  field: boolean;
  label: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <CheckItem size="xxsmall" label={params.label} selected={params.field} disabled={true} />
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

/// TODO ARIA and alt text
export const TabularDisplayImg = (params: {
  field: string;
  title?: string;
  width?: number;
  height?: number;
  // tslint:disable-next-line: no-any
  key?: any;
  debug?: boolean;
}) => {
  const w = params.width ? params.width : 20;
  const h = params.height ? params.height : 20;
  return (
    <TableCell key={params.key}>
      <span className={fonts.caption}>
        <img width={w} height={h} src={params.field} />
      </span>
    </TableCell>
  );
};

export const MultiSelectContainer = (params: {
  options: SelectOption[];
  selectedOptions: SelectOption[];
  title?: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const title = params.title ? <p className={fonts.caption}>{params.title}</p> : null;
  const element = (
    <Box>
      {title}
      <MultiSelect
        options={params.options}
        defaultValue={params.selectedOptions}
        isDisabled={true}
        isClearable={false}
      />
      {params.debug && <div>Current value:{params.selectedOptions.map(option => option.label).join()}</div>}
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const DisplaySelect = (params: {
  options: SelectOption[];
  selectedOption: SelectOption;
  title: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <Select options={params.options} value={params.selectedOption} isDisabled={true} isClearable={false} placeholder={''} />
      {params.debug && <div>Current value:{params.selectedOption.label}</div>}
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

interface TextInputObserverProps {
  field: FieldState<string | number>;
  type: InputType;
  title: string;
  comment?: string;
  placeholder: string;
  xsSpan: Sizes['xsSpan'];
  disabled?: boolean;
  isMandatory?: boolean;
  debug?: boolean;
}
export const TextInputObserver = ({
  field,
  type,
  title,
  comment,
  placeholder,
  xsSpan,
  disabled,
  isMandatory,
  debug,
}: TextInputObserverProps
) => {
  const element = (
    <Box>
      <p className={classnames(fonts.caption, isMandatory && styles.mandatory)}>{title}</p>
      {comment && <p className={fonts.captionSmall}>{comment}</p>}
      <TextInput type={type} state={field} placeholder={placeholder} disabled={disabled} />
      {debug && <div>Current value:{JSON.stringify(field)}</div>}
    </Box>
  );
  return GridWrapper({ xsSpan: xsSpan }, element, debug);
};

export const I18nTextInputObserver = (params: {
  lang: string;
  field: I18nInput;
  type: InputType;
  title: string;
  placeholder: string;
  xsSpan: Sizes['xsSpan'];
  textAreaRows?: number;
  textAreaCols?: number;
  debug?: boolean;
}) => {
  const rows = params.type === 'textarea' ? params.textAreaRows : undefined;
  const cols = params.type === 'textarea' ? params.textAreaCols : undefined;
  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <TextInput
        type={params.type}
        state={params.field[params.lang]}
        placeholder={params.placeholder}
        textAreaRows={rows}
        textAreaCols={cols}
      />
      {params.debug && <div>Current value:{JSON.stringify(params.field[params.lang])}</div>}
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const MarkdownEditorObserver = (params: {
  title: string;
  field: FieldState<string>;
  repo: RepositoryEntries;
  onUpload(req: RepositoryPutEntry): Promise<Entry>;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const onContentImageUpload = action(FileUtility.uploadImage(params.repo, params.onUpload));

  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <SimpleMDEReactContainer
        initialContent={params.field.value()}
        preview={true}
        onImageUpload={onContentImageUpload}
        onChange={params.field.onChange}
      />
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const I18nMarkdownEditorObserver = (params: {
  title: string;
  field: I18nInput;
  lang: LangCode;
  repo: RepositoryEntries;
  onUpload(req: RepositoryPutEntry): Promise<Entry>;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  /// Will this get into field after use?
  const inputField: FieldState<string> = params.field[params.lang] || newTextField();
  if (params.field[params.lang] === null) {
    params.field[params.lang] = inputField;
  }

  const onContentImageUpload = action(FileUtility.uploadImage(params.repo, params.onUpload));

  const element = (
    <Box>
      <p className={fonts.caption}>{params.title}</p>
      <SimpleMDEReactContainer
        initialContent={inputField.value()}
        preview={true}
        onImageUpload={onContentImageUpload}
        onChange={inputField.onChange}
      />
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const ImgObserver = (params: {
  field: FieldState<string>;
  type: InputType;
  title: string;
  placeholder: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const imgObserver = TextInputObserver(params);
  const imgDisplay = DisplayImg({
    field: params.field.value(),
    xsSpan: 2,
    debug: params.debug
  });

  return (
    <React.Fragment>
      {imgObserver}
      {imgDisplay}
    </React.Fragment>
  );
};

export const ReposImgObserver = (params: {
  field: FieldState<string>;
  path: FieldState<string>;
  type: InputType;
  title: string;
  placeholder: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const imgObserver = TextInputObserver({
    ...params,
    field: params.path
  });
  const imgDisplay = DisplayImg({
    field: params.field.value(),
    xsSpan: 2,
    debug: params.debug
  });

  return (
    <React.Fragment>
      {imgObserver}
      {imgDisplay}
    </React.Fragment>
  );
};

export const CheckItemObserver = (params: {
  field: boolean;
  label: string;
  xsSpan: Sizes['xsSpan'];
  // tslint:disable-next-line: no-any
  onChange(key?: any): void;
  disabled?: boolean;
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <CheckItem
        size="xxsmall"
        label={params.label}
        onChange={key => params.onChange(key)}
        selected={params.field}
        disabled={params.disabled}
      />
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const TabularCheckItemObserver = (params: {
  field: boolean;
  // tslint:disable: no-any
  id?: any;
  key?: any;
  onChange(id?: any): void;
  disabled?: boolean;
}) => {
  const toggle = (_onOff: boolean) => {
    params.onChange(params.id);
  };
  return (
    <TableCell key={params.key}>
      <span className={fonts.caption}>
        <CheckItem
          key={params.id}
          size="xxsmall"
          label=""
          inTable
          onChange={toggle}
          selected={params.field}
          disabled={params.disabled}
        />
      </span>
    </TableCell>
  );
};

export const TabularTextInputObserver = (params: {
  field: FieldState<string | number>;
  type: InputType;
  placeholder: string;
  id?: any;
  key?: any;
}) => {
  return (
    <TableCell key={params.key}>
      <span className={fonts.caption}>
        <TextInput key={params.id} type={params.type} state={params.field} placeholder={params.placeholder} />
      </span>
    </TableCell>
  );
};

interface SelectObserverProps {
  options: SelectOption[];
  selectedOption: SelectOption;
  title: string;
  xsSpan: Sizes['xsSpan'];
  onChange?(value?: SelectOption): void;
  disabled?: boolean;
  isMandatory?: boolean;
  debug?: boolean;
}

export const SelectObserver = ({
  options,
  selectedOption,
  title,
  xsSpan,
  onChange,
  disabled,
  isMandatory,
  debug,
}: SelectObserverProps) => {
  return (
    <SelectBox
      xsSpan={xsSpan}
      title={title}
      debug={debug}
      options={options}
      onChange={onChange}
      value={selectedOption}
      isDisabled={disabled}
      isMandatory={isMandatory}
    />
  );
};

export const MultiSelectObserver = (params: {
  options: SelectOption[];
  selectedOptions: SelectOption[];
  title: string;
  xsSpan: Sizes['xsSpan'];
  onChange?(value?: SelectOption[]): void;
  disabled?: boolean;
  debug?: boolean;
}) => {
  return (
    <MultiSelectBox
      xsSpan={params.xsSpan}
      title={params.title}
      debug={params.debug}
      options={params.options}
      onChange={params.onChange}
      defaultValue={params.selectedOptions}
      isDisabled={!!params.disabled}
    />
  );
};

export const CSVDropzoneObserver = (params: {
  files(files: File[]): void;
  contents(contents: FileContent[]): void;
  multiple: boolean;
  title: string;
  comment?: string;
  xsSpan: Sizes['xsSpan'];
  debug?: boolean;
}) => {
  const element = (
    <Box>
      <h5>{params.title}</h5>
      {params.comment && <p className={fonts.caption}>{params.comment}</p>}
      <CSVDropzone files={params.files} contents={params.contents} multiple={params.multiple} />
    </Box>
  );
  return GridWrapper({ xsSpan: params.xsSpan }, element, params.debug);
};

export const renderButtons = (
  onSave?: () => void,
  onSaveAs?: () => void,
  onCancel?: () => void,
  saveEnabled: () => boolean = () => true,
  saveAsEnabled: () => boolean = () => true,
  cancelEnabled: () => boolean = () => true
) => {
  let cancelButton = null;
  let saveButton = null;
  let saveAsButton = null;
  if (onCancel) {
    cancelButton = (
      <div className={styles.buttonList}>
        {/* TODO aria (or DOM) labels */}
        <Button
          className={styles.button}
          variant="round-secondary"
          onClick={async () => onCancel()}
          Icon={CloseIcon}
          size="small"
          disabled={!cancelEnabled()}
        >
          Cancel
        </Button>
      </div>
    );
  }
  if (onSave) {
    saveButton = (
      <div className={styles.buttonList}>
        <Button
          className={styles.button}
          onClick={async () => onSave()}
          Icon={TickCircleIcon}
          size="small"
          variant="round"
          disabled={!saveEnabled()}
        >
          Save
        </Button>
      </div>
    );
  }
  if (onSaveAs) {
    saveAsButton = (
      <div className={styles.buttonList}>
        <Button
          className={styles.button}
          onClick={async () => onSaveAs()}
          Icon={PlusIcon}
          size="small"
          variant="round"
          disabled={!saveAsEnabled()}
        >
          Clone/Save As...
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.buttonListContainer}>
      {cancelButton}
      {saveAsButton}
      {saveButton}
    </div>
  );
};

export const errorHandler = (onClick: () => void, title?: string, body?: string, key?: number) => {
  return (
    <Message
      key={key}
      // className={styles.callout}
      kind="error"
      title={title || 'Error'}
      body={body || 'Something went wrong'}
      button1={{
        label: 'Continue',
        onClick: async () => onClick()
      }}
    />
  );
};

export const okHandler = (
  onClick: () => void,
  title?: string,
  body?: string,
  children?: React.ReactText[],
  key?: number
) => {
  let i = 0;
  let otherData = null;
  if (children) {
    otherData = (
      <ul className={styles.messageList}>
        {children.map(child => (
          <li key={i++}>{child}</li>
        ))}
      </ul>
    );
  }
  return (
    <Message
      key={key}
      // className={styles.callout}
      title={title || 'OK'}
      body={body || 'Action completed successfully'}
      button1={{
        label: 'OK',
        onClick: async () => onClick()
      }}
    >
      {otherData}
    </Message>
  );
};

export const deleteHandler = (
  onDelete: () => Promise<void>,
  onCancel: () => void,
  typeName: string,
  namesToDelete: string[]
) => {
  let i = 0;
  const names = (
    <ul className={styles.messageList}>
      {namesToDelete.map(name => {
        return <li key={i++}>{name}</li>;
      })}
    </ul>
  );
  return (
    <Message
      className={styles.callout}
      title="Are you sure?"
      body={`Delete ${typeName}`}
      button1={{
        label: 'DELETE',
        onClick: onDelete
      }}
      button2={{
        label: 'Cancel',
        onClick: async () => onCancel()
      }}
    >
      {names}
    </Message>
  );
};



// WEBPACK FOOTER //
// ./src/admin/ui/common/editor.tsx